import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, CSSProperties } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { Collapsible, CollapsibleContent } from '@vs-shop/ui/collapsible';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The side-by-side comparison of several products: a column per product, a row
 * per thing being compared, and one left column naming what each row is.
 *
 *   <ProductComparisonBar variant={ProductComparisonBarVariant.SETTLED}>…</…>
 *
 *   <ProductComparison>
 *     <ProductComparisonGrid
 *       variant={ProductComparisonGridVariant.TABLE}
 *       style={productComparisonColumnStyle(products.length)}
 *     >
 *       <ProductComparisonRow variant={ProductComparisonRowVariant.PLAIN}>
 *         <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER}>…</…>
 *         <ProductComparisonValueCell
 *           variant={ProductComparisonValueCellVariant.PLAIN}
 *           drag={ProductComparisonDrag.IDLE}
 *         >…</ProductComparisonValueCell>
 *       </ProductComparisonRow>
 *     </ProductComparisonGrid>
 *   </ProductComparison>
 *
 * **The whole table is ONE grid, and every row dissolves itself into it.**
 * `ProductComparisonGrid` declares the only column template there is —
 * `repeat(var(--product-count), …)` beside a fixed left column — and every row
 * part renders `display: contents`, so its cells become direct items of that
 * grid rather than of the row. That is what keeps a column the same width from
 * the header to the last attribute, and what lets the left column stay
 * `sticky` across the whole table.
 *
 * It is also why the row parts own their dissolution rather than taking it as a
 * class: a composition that forgot `contents` would render a box per row, break
 * every column's alignment, and change no class anyone could see in a diff.
 * The gate that sees it is geometric — `productComparison.browser.test.tsx`.
 *
 * How many columns there are rides the native `style` prop as the custom
 * property the template reads. {@link productComparisonColumnStyle} builds that
 * value with its type stated rather than cast.
 *
 * **What is NOT here:** every string, every price, every image URL, the
 * comparison cookie, the drag-and-drop plumbing, and which rows a filter hides.
 * A row a filter removes is a row the composition does not render — no shared
 * part returns nothing, and no shared part reads state.
 *
 * `variant` is the appearance axis, required wherever a part has one, with no
 * `defaultVariants` anywhere: an appearance decided by omission is invisible in
 * review. `drag` is the second axis, and it is the same kind of thing — what a
 * column looks like while it is being carried, or while it is the place a
 * carried column would land.
 *
 * Server-first: every part here is a plain component. The controls a shopper
 * presses — the checkbox, the group toggle, the remove and add-to-cart buttons
 * — are the tenant's, composed into these parts as children.
 */

/* ── how many columns ─────────────────────────────────────────────────── */

/**
 * The column count the grid template repeats over, as a `style` value.
 *
 * A custom property is not part of React's `CSSProperties`, so the value needs
 * a type that says it carries one. It is declared, never cast: a cast would
 * also silence a genuinely wrong value.
 */
export type ProductComparisonColumnStyle = CSSProperties & {
  readonly '--product-count': number;
};

/** The `style` value a comparison grid of `count` products takes. */
export const productComparisonColumnStyle = (count: number): ProductComparisonColumnStyle => ({
  '--product-count': count,
});

/* ── the table ────────────────────────────────────────────────────────── */

/**
 * The frame the table scrolls inside.
 *
 * `isolate` is load-bearing: the left column and the group headers stack
 * against each other inside the table, and without a stacking context of its
 * own the table's `sticky` cells would compete with whatever the page puts
 * beside it.
 */
const productComparisonClasses = 'relative isolate overflow-x-auto rounded-lg';

export type ProductComparisonProps = ComponentProps<'div'>;

/** The frame the table scrolls inside. */
export const ProductComparison = ({ className, ...props }: ProductComparisonProps) => (
  <div
    data-slot="product-comparison"
    className={clsxm(productComparisonClasses, className)}
    {...props}
  />
);

/**
 * The one grid every cell of the comparison lands in.
 *
 * Two appearances, because the comparison is shown twice: the table itself, and
 * the condensed bar that follows the shopper down the page. They share the
 * column template — that is the point, the bar's columns line up with the
 * table's — and differ in the box around it.
 */
export const productComparisonGridVariants = cva(
  'lap:grid-cols-[260px_repeat(var(--product-count),340px)] grid grid-cols-[150px_repeat(var(--product-count),240px)]',
  {
    variants: {
      variant: {
        /** The table: as wide as its columns, clipped to its own rounded border. */
        table: 'border-foreground/10 w-max overflow-clip rounded-lg border',
        /** The condensed bar: one fixed-height row of the same columns. */
        bar: 'h-[105px]',
        /** The opt-out: a tenant framing the grid itself. */
        unstyled: '',
      },
    },
  }
);

type ProductComparisonGridVariantName = NonNullable<
  VariantProps<typeof productComparisonGridVariants>['variant']
>;

export type ProductComparisonGridVariant = ProductComparisonGridVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonGridVariant = {
  /** The table itself. */
  TABLE: 'table',
  /** The condensed bar. */
  BAR: 'bar',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonGridVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductComparisonGridVariantProps = {
  readonly variant: ProductComparisonGridVariant;
};

export type ProductComparisonGridProps = ComponentProps<'div'> & ProductComparisonGridVariantProps;

/**
 * The grid. Its column count arrives through `style`, as
 * {@link productComparisonColumnStyle} builds it.
 */
export const ProductComparisonGrid = ({
  className,
  variant,
  ...props
}: ProductComparisonGridProps) => (
  <div
    data-slot="product-comparison-grid"
    className={clsxm(productComparisonGridVariants({ variant }), className)}
    {...props}
  />
);

/**
 * One row of the comparison.
 *
 * It renders no box: `contents` dissolves it so its cells become items of the
 * table's single grid. The two appearances differ only in whether the row lights
 * its own cells up under the pointer, which it does by being the `group` those
 * cells' `group-hover:` steps resolve against.
 */
export const productComparisonRowVariants = cva('contents', {
  variants: {
    variant: {
      /** A row that does not react to the pointer. */
      plain: '',
      /** A row whose cells light up together under the pointer. */
      hoverable: 'group',
      /**
       * The same, set in the quiet ink: the attribute rows, whose left label
       * takes its colour from the row rather than from the cell — the label
       * cell names no colour of its own, so the row is where this can be said.
       */
      hoverableMuted: 'group text-foreground-muted',
      /** The opt-out: a tenant that wants the dissolution and nothing else. */
      unstyled: '',
    },
  },
});

type ProductComparisonRowVariantName = NonNullable<
  VariantProps<typeof productComparisonRowVariants>['variant']
>;

export type ProductComparisonRowVariant = ProductComparisonRowVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonRowVariant = {
  /** A row that does not react to the pointer. */
  PLAIN: 'plain',
  /** A row whose cells light up together. */
  HOVERABLE: 'hoverable',
  /** The same, in the quiet ink. */
  HOVERABLE_MUTED: 'hoverableMuted',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonRowVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductComparisonRowVariantProps = {
  readonly variant: ProductComparisonRowVariant;
};

export type ProductComparisonRowProps = ComponentProps<'div'> & ProductComparisonRowVariantProps;

/** One row of the comparison, dissolved into the table's grid. */
export const ProductComparisonRow = ({
  className,
  variant,
  ...props
}: ProductComparisonRowProps) => (
  <div
    data-slot="product-comparison-row"
    className={clsxm(productComparisonRowVariants({ variant }), className)}
    {...props}
  />
);

/**
 * A row whose product cells are a LIST — the two rows that offer an action per
 * product rather than state a fact about it.
 *
 * Dissolved like every other row: the list marks up what the cells are, and the
 * grid still lays them out.
 */
const productComparisonRowListClasses = 'contents';

export type ProductComparisonRowListProps = ComponentProps<'ul'>;

/** The list a row of per-product actions is. */
export const ProductComparisonRowList = ({
  className,
  ...props
}: ProductComparisonRowListProps) => (
  <ul
    data-slot="product-comparison-row-list"
    className={clsxm(productComparisonRowListClasses, className)}
    {...props}
  />
);

/**
 * The hairline the page watches to decide whether the condensed bar is due.
 *
 * It spans every column and takes no height a shopper can see. It is a part
 * rather than the composition's own `<div>` because it has to be a grid item of
 * this grid — anywhere else it would report a different position entirely.
 */
const productComparisonSentinelClasses = 'pointer-events-none col-span-full h-[0.5px]';

export type ProductComparisonSentinelProps = ComponentProps<'div'>;

/** The hairline that says where the table's header ends. */
export const ProductComparisonSentinel = ({
  className,
  ...props
}: ProductComparisonSentinelProps) => (
  <div
    data-slot="product-comparison-sentinel"
    aria-hidden="true"
    className={clsxm(productComparisonSentinelClasses, className)}
    {...props}
  />
);

/* ── what a column looks like while it is being moved ─────────────────── */

/**
 * TWO axes, because they are two independent facts and a column can carry both
 * at once: it is the column being carried, AND the pointer is over it.
 *
 * The gesture itself — which pointer events fire, which cookie is rewritten —
 * is the tenant's. What crosses is only what it looks like.
 */

/** Whether this column is the one being carried. */
const DRAG_ARMS = {
  /** Nothing is being carried, or this is not the column being carried. */
  idle: '',
  /** This column is in the air: it steps back while it travels. */
  lifted: 'opacity-40',
} as const;

/** Whether this column is where a carried column would land. */
const DROP_ARMS = {
  /** The pointer is somewhere else. */
  idle: '',
  /** A carried column would land here. */
  receiving: 'bg-background-intense',
} as const;

/** The looks a column takes while it is being carried. */
export const ProductComparisonDrag = {
  /** Not the column being carried. */
  IDLE: 'idle',
  /** The column being carried. */
  LIFTED: 'lifted',
} as const satisfies Record<string, keyof typeof DRAG_ARMS>;

export type ProductComparisonDrag =
  (typeof ProductComparisonDrag)[keyof typeof ProductComparisonDrag];

/** The looks a column takes while it is a landing place. */
export const ProductComparisonDrop = {
  /** The pointer is somewhere else. */
  IDLE: 'idle',
  /** A carried column would land here. */
  RECEIVING: 'receiving',
} as const satisfies Record<string, keyof typeof DROP_ARMS>;

export type ProductComparisonDrop =
  (typeof ProductComparisonDrop)[keyof typeof ProductComparisonDrop];

/** Required and closed, like every axis here. */
export type ProductComparisonDragProps = { readonly drag: ProductComparisonDrag };

/** Required and closed, like every axis here. */
export type ProductComparisonDropProps = { readonly drop: ProductComparisonDrop };

/* ── the cells ────────────────────────────────────────────────────────── */

/**
 * The left column's cell: what the row beside it is about.
 *
 * `sticky left-0` is why the label survives a table scrolled sideways, and the
 * two appearances differ in how high they stack — a header label stands over
 * the group headings, an attribute label under them.
 */
export const productComparisonLabelCellVariants = cva(
  'border-foreground/10 bg-background-intense-more lap:px-4 lap:py-5 lap:text-base sticky left-0 flex items-center border-b px-2 py-2.5 text-sm',
  {
    variants: {
      variant: {
        /** A label over the head of the table. */
        header: 'text-foreground-muted z-20',
        /**
         * The same, with no rule under it: the head's own rows run together as
         * one block, and only the last of them is ruled off from the
         * attributes below.
         */
        headerOpen: 'text-foreground-muted z-20 border-0',
        /** Unruled, and its contents held at the top — the row carrying the filter. */
        headerOpenTop: 'text-foreground-muted z-20 items-start border-b-0',
        /** Unruled and flush, so the control inside it sets the row's height. */
        headerOpenFlush: 'text-foreground-muted lap:py-0 z-20 border-b-0 py-0',
        /** A label naming one compared attribute. */
        attribute: 'z-10 text-left',
        /** The opt-out: a tenant painting the label itself. */
        unstyled: '',
      },
    },
  }
);

type ProductComparisonLabelCellVariantName = NonNullable<
  VariantProps<typeof productComparisonLabelCellVariants>['variant']
>;

export type ProductComparisonLabelCellVariant = ProductComparisonLabelCellVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonLabelCellVariant = {
  /** A label over the head of the table. */
  HEADER: 'header',
  /** The same, unruled. */
  HEADER_OPEN: 'headerOpen',
  /** Unruled, contents at the top. */
  HEADER_OPEN_TOP: 'headerOpenTop',
  /** Unruled and flush. */
  HEADER_OPEN_FLUSH: 'headerOpenFlush',
  /** A label naming one compared attribute. */
  ATTRIBUTE: 'attribute',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonLabelCellVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductComparisonLabelCellVariantProps = {
  readonly variant: ProductComparisonLabelCellVariant;
};

export type ProductComparisonLabelCellProps = ComponentProps<'div'> &
  ProductComparisonLabelCellVariantProps;

/** The left column's cell. */
export const ProductComparisonLabelCell = ({
  className,
  variant,
  ...props
}: ProductComparisonLabelCellProps) => (
  <div
    data-slot="product-comparison-label-cell"
    className={clsxm(productComparisonLabelCellVariants({ variant }), className)}
    {...props}
  />
);

/**
 * A product column's cell in the head of the table: its photo, its name, its
 * price, whether it is in stock, whose it is.
 *
 * Three appearances, and they are three shapes of box rather than three
 * decorations: the photo's place is tall and carries no rule under it, the
 * price is set in the heading face, everything else is the plain cell.
 */
export const productComparisonValueCellVariants = cva(
  'border-foreground/10 bg-background text-foreground-muted group-hover:bg-background-intense-more lap:px-6 lap:py-5 lap:text-base flex justify-center border-b px-3 py-2.5 text-sm transition-opacity',
  {
    variants: {
      variant: {
        /** A fact about the product, set in the body face. */
        plain: '',
        /** The photograph's place: tall, and open to the row under it. */
        media: 'lap:h-[187px] h-[130px] border-b-0',
        /** The price, set in the heading face at the table's largest step. */
        figure: 'font-heading text-foreground lap:text-xl text-lg tracking-tight',
        /** The opt-out: a tenant painting the cell itself. */
        unstyled: '',
      },
      drag: DRAG_ARMS,
      drop: DROP_ARMS,
    },
  }
);

type ProductComparisonValueCellVariantName = NonNullable<
  VariantProps<typeof productComparisonValueCellVariants>['variant']
>;

export type ProductComparisonValueCellVariant = ProductComparisonValueCellVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonValueCellVariant = {
  /** A fact about the product. */
  PLAIN: 'plain',
  /** The photograph's place. */
  MEDIA: 'media',
  /** The price. */
  FIGURE: 'figure',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonValueCellVariantName>;

/** Required and closed — not `VariantProps`, which makes both axes optional. */
export type ProductComparisonValueCellVariantProps = {
  readonly variant: ProductComparisonValueCellVariant;
};

export type ProductComparisonValueCellProps = ComponentProps<'div'> &
  ProductComparisonValueCellVariantProps &
  ProductComparisonDragProps &
  ProductComparisonDropProps;

/** A product column's cell in the head of the table. */
export const ProductComparisonValueCell = ({
  className,
  variant,
  drag,
  drop,
  ...props
}: ProductComparisonValueCellProps) => (
  <div
    data-slot="product-comparison-value-cell"
    className={clsxm(productComparisonValueCellVariants({ variant, drag, drop }), className)}
    {...props}
  />
);

/**
 * A product column's cell in an attribute row.
 *
 * Its own appearance says whether the row it stands in is telling the shopper
 * anything: a row every product answers the same way is still shown, and still
 * readable, but it steps back so the rows that differ read first.
 */
export const productComparisonAttributeCellVariants = cva(
  'border-foreground/10 bg-background group-hover:bg-background-intense-more lap:px-4 lap:py-5 lap:text-base flex flex-1 border-b px-2 py-2.5 text-sm',
  {
    variants: {
      variant: {
        /** A row whose products answer differently: the row that carries news. */
        distinct: '',
        /** A row every product answers alike: present, and quieter. */
        uniform: 'text-foreground/50',
        /** The opt-out: a tenant painting the cell itself. */
        unstyled: '',
      },
    },
  }
);

type ProductComparisonAttributeCellVariantName = NonNullable<
  VariantProps<typeof productComparisonAttributeCellVariants>['variant']
>;

export type ProductComparisonAttributeCellVariant = ProductComparisonAttributeCellVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonAttributeCellVariant = {
  /** A row whose products answer differently. */
  DISTINCT: 'distinct',
  /** A row every product answers alike. */
  UNIFORM: 'uniform',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonAttributeCellVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductComparisonAttributeCellVariantProps = {
  readonly variant: ProductComparisonAttributeCellVariant;
};

export type ProductComparisonAttributeCellProps = ComponentProps<'div'> &
  ProductComparisonAttributeCellVariantProps;

/** A product column's cell in an attribute row. */
export const ProductComparisonAttributeCell = ({
  className,
  variant,
  ...props
}: ProductComparisonAttributeCellProps) => (
  <div
    data-slot="product-comparison-attribute-cell"
    className={clsxm(productComparisonAttributeCellVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The value inside an attribute cell, centred in its column.
 *
 * Its own box rather than the cell's padding: a value that is a mark rather
 * than a word — a tick, a cross — has to sit in the middle of the column, and
 * the cell around it holds the row's rule and background.
 */
const productComparisonAttributeValueClasses =
  'flex flex-1 items-center justify-center px-3 lap:px-6';

export type ProductComparisonAttributeValueProps = ComponentProps<'div'>;

/** The value inside an attribute cell. */
export const ProductComparisonAttributeValue = ({
  className,
  ...props
}: ProductComparisonAttributeValueProps) => (
  <div
    data-slot="product-comparison-attribute-value"
    className={clsxm(productComparisonAttributeValueClasses, className)}
    {...props}
  />
);

/**
 * The cell of a row that offers an action per product — one item of
 * {@link ProductComparisonRowList}.
 */
export const productComparisonItemCellVariants = cva(
  'border-b-foreground/10 bg-background flex flex-1 items-center justify-center border-b py-6 transition-opacity',
  {
    variants: {
      drag: DRAG_ARMS,
      drop: DROP_ARMS,
    },
  }
);

export type ProductComparisonItemCellProps = ComponentProps<'li'> &
  ProductComparisonDragProps &
  ProductComparisonDropProps;

/** One product's action cell. */
export const ProductComparisonItemCell = ({
  className,
  drag,
  drop,
  ...props
}: ProductComparisonItemCellProps) => (
  <li
    data-slot="product-comparison-item-cell"
    className={clsxm(productComparisonItemCellVariants({ drag, drop }), className)}
    {...props}
  />
);

/**
 * The closing cell of a product's column: its name and price restated with the
 * action beside them, so a shopper who has read to the bottom of a long table
 * still knows which column they are in.
 */
const productComparisonSummaryCellClasses =
  'flex flex-col items-center justify-between gap-5 bg-background px-3 py-4 font-heading lap:px-6 lap:py-6';

export type ProductComparisonSummaryCellProps = ComponentProps<'li'>;

/** The closing cell of a product's column. */
export const ProductComparisonSummaryCell = ({
  className,
  ...props
}: ProductComparisonSummaryCellProps) => (
  <li
    data-slot="product-comparison-summary-cell"
    className={clsxm(productComparisonSummaryCellClasses, className)}
    {...props}
  />
);

/** The name and price inside a summary cell, stacked and centred. */
const productComparisonSummaryCopyClasses = 'flex flex-col gap-2 text-center lap:text-xl';

export type ProductComparisonSummaryCopyProps = ComponentProps<'div'>;

/** The name and price inside a summary cell. */
export const ProductComparisonSummaryCopy = ({
  className,
  ...props
}: ProductComparisonSummaryCopyProps) => (
  <div
    data-slot="product-comparison-summary-copy"
    className={clsxm(productComparisonSummaryCopyClasses, className)}
    {...props}
  />
);

/** A restated product name: two lines at most, whatever its length. */
const productComparisonSummaryNameClasses = 'line-clamp-2';

export type ProductComparisonSummaryNameProps = ComponentProps<'span'>;

/** A restated product name. */
export const ProductComparisonSummaryName = ({
  className,
  ...props
}: ProductComparisonSummaryNameProps) => (
  <span
    data-slot="product-comparison-summary-name"
    className={clsxm(productComparisonSummaryNameClasses, className)}
    {...props}
  />
);

/**
 * A product's name in the head of the table.
 *
 * No heading element: a comparison is furniture a page places, and a name
 * inside it competes with the page's own outline. The element is the page's,
 * through `render` — which is also how the name becomes the link it is on both
 * tenants, without this package naming an anchor.
 */
const productComparisonNameClasses =
  'font-heading text-lg tracking-tight text-foreground hover:underline lap:text-xl';

export type ProductComparisonNameProps = ComponentProps<'div'> & {
  /** The element to paint instead of the default `<div>` — usually a link. */
  readonly render?: RenderElement;
};

/** A product's name in the head of the table. */
export const ProductComparisonName = ({
  className,
  render,
  ...props
}: ProductComparisonNameProps) => (
  <RenderedPart
    dataSlot="product-comparison-name"
    className={clsxm(productComparisonNameClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * How a product's photograph sits in the head of the table — a recipe applied
 * to the image the tenant renders, not a part.
 *
 * A part would have to render the image element itself, and the portable image
 * seam (`@vs-shop/image-link`) declares `next/image`'s prop surface, which
 * carries no `data-slot`. The photograph also needs no frame of its own: the
 * cell it stands in IS the frame, and it is the cell that changes height
 * between the phone and the laptop, so a column with no photograph puts its
 * stand-in in the same box.
 */
export const productComparisonImageClasses = 'h-full w-full object-contain';

/* ── the controls the head of the table carries ───────────────────────── */

/**
 * The label of the control that hides every row the products answer alike.
 *
 * A `<label>` rather than a handler on the row: pointing it at the checkbox is
 * what makes the words a click target, and `items-start` keeps the box level
 * with the first line of a label that wraps. What the control IS — its checked
 * state, its handler — belongs to the composition, which puts
 * `@vs-shop/ui/checkbox` inside this.
 */
const productComparisonFilterLabelClasses =
  'flex cursor-pointer items-start gap-2 tracking-tighter select-none';

export type ProductComparisonFilterLabelProps = ComponentProps<'label'>;

/** The label of the differences-only control. */
export const ProductComparisonFilterLabel = ({
  className,
  ...props
}: ProductComparisonFilterLabelProps) => (
  <label
    data-slot="product-comparison-filter-label"
    className={clsxm(productComparisonFilterLabelClasses, className)}
    {...props}
  />
);

/**
 * The cell a column is carried by: the handle on one side, the way to drop the
 * column from the comparison on the other.
 *
 * This is where the landing place shows itself — a dashed outline drawn inside
 * the cell rather than around it, so the table's grid does not move while a
 * column is in the air.
 */
export const productComparisonActionsCellVariants = cva(
  'border-foreground/10 bg-background text-foreground-muted group-hover:bg-background-intense-more hover:bg-primary/5 lap:px-0 lap:py-0 lap:pr-6 lap:text-base relative flex justify-between border-b-0 px-0 py-0 pr-3 text-sm',
  {
    variants: {
      drop: {
        /** The pointer is somewhere else. */
        idle: '',
        /** A carried column would land here. */
        receiving:
          'bg-primary/10 before:border-primary before:absolute before:inset-0 before:border-2 before:border-dashed',
      },
    },
  }
);

export type ProductComparisonActionsCellProps = ComponentProps<'div'> & ProductComparisonDropProps;

/**
 * The cell a column is carried by.
 *
 * It takes the landing axis and not the carrying one: the carried column does
 * not fade here, unlike every other cell of that column, because the handle it
 * is being carried by has to stay legible under the pointer.
 */
export const ProductComparisonActionsCell = ({
  className,
  drop,
  ...props
}: ProductComparisonActionsCellProps) => (
  <div
    data-slot="product-comparison-actions-cell"
    className={clsxm(productComparisonActionsCellVariants({ drop }), className)}
    {...props}
  />
);

/**
 * The handle a column is carried by.
 *
 * It fills its half of the cell rather than being a mark inside it, so the
 * grab target is the whole area beside the mark and not the 20 pixels of the
 * mark itself.
 */
export const productComparisonDragHandleVariants = cva(
  'group lap:py-5 lap:pl-6 flex flex-1 cursor-grab py-2.5 pl-3 transition-all active:scale-95 active:cursor-grabbing',
  {
    variants: {
      drag: {
        /** Not the column being carried. */
        idle: '',
        /** This column is in the air: the pointer keeps the carrying cursor. */
        lifted: 'cursor-grabbing',
      },
    },
  }
);

export type ProductComparisonDragHandleProps = ComponentProps<'div'> & ProductComparisonDragProps;

/** The handle a column is carried by. */
export const ProductComparisonDragHandle = ({
  className,
  drag,
  ...props
}: ProductComparisonDragHandleProps) => (
  <div
    data-slot="product-comparison-drag-handle"
    className={clsxm(productComparisonDragHandleVariants({ drag }), className)}
    {...props}
  />
);

/**
 * The mark inside the handle, as a recipe rather than a part: the mark itself
 * is an icon the tenant chooses, and what crosses is how it answers the
 * gesture — it grows under the pointer, shrinks as the column is picked up, and
 * turns the brand colour over a landing place.
 */
export const productComparisonDragIconVariants = cva(
  'text-foreground-muted group-hover:text-primary size-5 transition-all duration-200 group-hover:scale-110 group-active:scale-90',
  {
    variants: {
      drop: {
        /** The pointer is somewhere else. */
        idle: '',
        /** A carried column would land here. */
        receiving: 'text-primary scale-110',
      },
    },
  }
);

/**
 * The way one column leaves the comparison, beside the handle it is carried by.
 *
 * A plain `<button>` the tenant hangs its own atom on, so the flow is the
 * shop's and the place and the paint are the section's. The mark goes to the
 * warning colour under a pointer, because this is the control that takes a
 * product away.
 */
export type ProductComparisonRemoveProps = ComponentProps<'button'>;

/** A column's remove control. */
export const ProductComparisonRemove = ({
  className,
  type,
  ...props
}: ProductComparisonRemoveProps) => (
  <button
    data-slot="product-comparison-remove"
    type={type ?? 'button'}
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type ProductComparisonRemoveIconProps = ComponentProps<'div'> & {
  /** The icon to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** A column's remove mark. */
export const ProductComparisonRemoveIcon = ({
  className,
  render,
  ...props
}: ProductComparisonRemoveIconProps) => (
  <RenderedPart
    dataSlot="product-comparison-remove-icon"
    className={clsxm('text-foreground hover:text-destructive size-5 transition-colors', className)}
    render={render}
    {...props}
  />
);

/**
 * The way every column leaves at once, standing in the head's left cell.
 *
 * Short and the full measure of that cell, because the cell is the narrow
 * sticky column and the control has to fit it at every step.
 */
export type ProductComparisonClearAllProps = ButtonProps;

/** The control that empties the comparison. */
export const ProductComparisonClearAll = ({
  className,
  ...props
}: ProductComparisonClearAllProps) => (
  <Button
    size="sm"
    data-slot="product-comparison-clear-all"
    className={clsxm('border-foreground/10 lap:h-10 lap:gap-2 h-8 w-full gap-1', className)}
    {...props}
  />
);

/** Its label, a step under the table's own copy so the control stays quiet. */
export type ProductComparisonClearAllLabelProps = ComponentProps<'span'>;

/** The clear control's label. */
export const ProductComparisonClearAllLabel = ({
  className,
  ...props
}: ProductComparisonClearAllLabelProps) => (
  <span
    data-slot="product-comparison-clear-all-label"
    className={clsxm(
      'font-heading',
      'lap:tracking-extra-wide lap:text-base/5 text-sm/4 tracking-normal',
      className
    )}
    {...props}
  />
);

/* ── the attribute groups ─────────────────────────────────────────────── */

/**
 * One named group of attributes, and everything under it.
 *
 * Dissolved like a row, because its heading and its rows are all items of the
 * table's one grid. Composed over `@vs-shop/ui`'s collapsible: the open state is
 * the primitive's, so a tenant that reorders these parts changes nothing about
 * the wiring.
 */
const productComparisonGroupClasses = 'contents';

export type ProductComparisonGroupProps = ComponentProps<typeof Collapsible>;

/** One named group of attributes. */
export const ProductComparisonGroup = ({ className, ...props }: ProductComparisonGroupProps) => (
  <Collapsible
    data-slot="product-comparison-group"
    className={clsxm(productComparisonGroupClasses, className)}
    {...props}
  />
);

/**
 * A group's heading, in the left column, and the control that folds the group
 * away.
 *
 * It stacks under the header labels and over the attribute labels, which is
 * what lets a sideways-scrolled table show the group name over its own rows.
 */
const productComparisonGroupToggleClasses =
  'sticky left-0 z-10 flex w-full items-center gap-2 bg-background-inverted px-2 py-2.5 text-left text-foreground-inverted lap:px-4 lap:py-5';

export type ProductComparisonGroupToggleProps = ComponentProps<'button'>;

/** A group's heading and folding control. */
export const ProductComparisonGroupToggle = ({
  className,
  ...props
}: ProductComparisonGroupToggleProps) => (
  <button
    type="button"
    data-slot="product-comparison-group-toggle"
    className={clsxm(productComparisonGroupToggleClasses, className)}
    {...props}
  />
);

/**
 * The group's name.
 *
 * A neutral element, not a heading: the page owns its own outline, and a table
 * of parameters contributing a run of headings to it is a page about
 * parameters.
 */
const productComparisonGroupLabelClasses = 'font-heading text-base lap:text-lg/[22px]';

export type ProductComparisonGroupLabelProps = ComponentProps<'span'>;

/** The group's name. */
export const ProductComparisonGroupLabel = ({
  className,
  ...props
}: ProductComparisonGroupLabelProps) => (
  <span
    data-slot="product-comparison-group-label"
    className={clsxm(productComparisonGroupLabelClasses, className)}
    {...props}
  />
);

/**
 * The mark on a group heading, as a recipe: which mark it is stays the
 * tenant's, how it answers the fold is the section's.
 */
export const productComparisonGroupChevronVariants = cva(
  'lap:size-5 size-4 shrink-0 transition-transform',
  {
    variants: {
      state: {
        /** The group is folded away. */
        collapsed: '',
        /** The group is open: the mark points back at its own heading. */
        expanded: 'rotate-180',
      },
    },
  }
);

type ProductComparisonGroupStateName = NonNullable<
  VariantProps<typeof productComparisonGroupChevronVariants>['state']
>;

export type ProductComparisonGroupState = ProductComparisonGroupStateName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonGroupState = {
  /** Folded away. */
  COLLAPSED: 'collapsed',
  /** Open. */
  EXPANDED: 'expanded',
} as const satisfies Record<string, ProductComparisonGroupStateName>;

/**
 * The part of a group heading that stands in a product's column.
 *
 * It carries the heading's band across the table and folds the group when it is
 * pressed, so the whole width of the heading is a control rather than only the
 * words on the left.
 */
const productComparisonGroupSpacerClasses =
  'cursor-pointer bg-background-inverted-intense text-foreground-inverted';

export type ProductComparisonGroupSpacerProps = ComponentProps<'button'>;

/** A group heading's continuation into one product column. */
export const ProductComparisonGroupSpacer = ({
  className,
  ...props
}: ProductComparisonGroupSpacerProps) => (
  <button
    type="button"
    data-slot="product-comparison-group-spacer"
    className={clsxm(productComparisonGroupSpacerClasses, className)}
    {...props}
  />
);

/** The rows a group holds, dissolved into the table's grid like every row. */
const productComparisonGroupContentClasses = 'contents';

export type ProductComparisonGroupContentProps = ComponentProps<typeof CollapsibleContent>;

/** The rows a group holds. */
export const ProductComparisonGroupContent = ({
  className,
  ...props
}: ProductComparisonGroupContentProps) => (
  <CollapsibleContent
    data-slot="product-comparison-group-content"
    className={clsxm(productComparisonGroupContentClasses, className)}
    {...props}
  />
);

/* ── the condensed bar ────────────────────────────────────────────────── */

/**
 * The comparison condensed to one row, pinned to the top of the window once the
 * table's own head has scrolled away.
 *
 * Only from the laptop step up: on a phone it would take a third of the screen
 * to say what the table says. Both appearances are rendered — the bar animates
 * between them, so the settled one is off the top of the window rather than
 * absent from the document.
 */
export const productComparisonBarVariants = cva(
  'bg-background-intense shadow-foreground/20 lap:block fixed top-0 right-0 left-0 z-50 hidden shadow-md transition-all duration-300',
  {
    variants: {
      variant: {
        /** Down over the page. */
        raised: 'translate-y-0 opacity-100',
        /** Off the top of the window, where the table's own head still stands. */
        settled: '-translate-y-full opacity-0',
        /** The opt-out: a tenant placing the bar itself. */
        unstyled: '',
      },
    },
  }
);

type ProductComparisonBarVariantName = NonNullable<
  VariantProps<typeof productComparisonBarVariants>['variant']
>;

export type ProductComparisonBarVariant = ProductComparisonBarVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductComparisonBarVariant = {
  /** Down over the page. */
  RAISED: 'raised',
  /** Off the top of the window. */
  SETTLED: 'settled',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductComparisonBarVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductComparisonBarVariantProps = {
  readonly variant: ProductComparisonBarVariant;
};

export type ProductComparisonBarProps = ComponentProps<'div'> & ProductComparisonBarVariantProps;

/** The comparison condensed to one row. */
export const ProductComparisonBar = ({
  className,
  variant,
  ...props
}: ProductComparisonBarProps) => (
  <div
    data-slot="product-comparison-bar"
    className={clsxm(productComparisonBarVariants({ variant }), className)}
    {...props}
  />
);

/** The frame the bar's own columns scroll inside, mirroring the table's. */
const productComparisonBarScrollerClasses = 'overflow-x-auto';

export type ProductComparisonBarScrollerProps = ComponentProps<'div'>;

/** The frame the bar's columns scroll inside. */
export const ProductComparisonBarScroller = ({
  className,
  ...props
}: ProductComparisonBarScrollerProps) => (
  <div
    data-slot="product-comparison-bar-scroller"
    className={clsxm(productComparisonBarScrollerClasses, className)}
    {...props}
  />
);

/** One product's place in the bar: its photograph beside its name and price. */
export const productComparisonBarCellVariants = cva('flex gap-3.5 px-4 py-3 transition-opacity', {
  variants: {
    drag: DRAG_ARMS,
    drop: DROP_ARMS,
  },
});

export type ProductComparisonBarCellProps = ComponentProps<'div'> &
  ProductComparisonDragProps &
  ProductComparisonDropProps;

/** One product's place in the bar. */
export const ProductComparisonBarCell = ({
  className,
  drag,
  drop,
  ...props
}: ProductComparisonBarCellProps) => (
  <div
    data-slot="product-comparison-bar-cell"
    className={clsxm(productComparisonBarCellVariants({ drag, drop }), className)}
    {...props}
  />
);

/**
 * The photograph's place in the bar: a fixed box, so every column's name starts
 * at the same distance from the left of its cell whether or not there is a
 * photograph to show.
 */
const productComparisonBarMediaClasses = 'flex h-[61px] w-[107px] items-center justify-center';

export type ProductComparisonBarMediaProps = ComponentProps<'div'>;

/** The photograph's place in the bar. */
export const ProductComparisonBarMedia = ({
  className,
  ...props
}: ProductComparisonBarMediaProps) => (
  <div
    data-slot="product-comparison-bar-media"
    className={clsxm(productComparisonBarMediaClasses, className)}
    {...props}
  />
);

/** The name and price in a bar cell, held apart down the cell's height. */
const productComparisonBarCopyClasses = 'flex flex-col justify-between gap-2';

export type ProductComparisonBarCopyProps = ComponentProps<'div'>;

/** The name and price in a bar cell. */
export const ProductComparisonBarCopy = ({
  className,
  ...props
}: ProductComparisonBarCopyProps) => (
  <div
    data-slot="product-comparison-bar-copy"
    className={clsxm(productComparisonBarCopyClasses, className)}
    {...props}
  />
);

/**
 * A product's name in the bar: two lines at most, in the bar's smaller step.
 * The element is the page's, through `render`, exactly as in the table.
 */
const productComparisonBarNameClasses = 'font-heading line-clamp-2 text-base/5 hover:underline';

export type ProductComparisonBarNameProps = ComponentProps<'div'> & {
  /** The element to paint instead of the default `<div>` — usually a link. */
  readonly render?: RenderElement;
};

/** A product's name in the bar. */
export const ProductComparisonBarName = ({
  className,
  render,
  ...props
}: ProductComparisonBarNameProps) => (
  <RenderedPart
    dataSlot="product-comparison-bar-name"
    className={clsxm(productComparisonBarNameClasses, className)}
    render={render}
    {...props}
  />
);

/** A product's price in the bar, at the bar's smallest step. */
const productComparisonBarPriceClasses = 'text-sm';

export type ProductComparisonBarPriceProps = ComponentProps<'div'>;

/** A product's price in the bar. */
export const ProductComparisonBarPrice = ({
  className,
  ...props
}: ProductComparisonBarPriceProps) => (
  <div
    data-slot="product-comparison-bar-price"
    className={clsxm(productComparisonBarPriceClasses, className)}
    {...props}
  />
);

/* ── the footer ───────────────────────────────────────────────────────── */

/**
 * The bar of actions under the table: what clears the comparison on one side,
 * what buys all of it on the other.
 *
 * Stacked on a phone, and a row from the tablet step up — where the two
 * actions stand at opposite ends of the table's width.
 */
const productComparisonFooterClasses =
  'flex flex-col justify-between gap-4 tab:flex-1 tab:flex-row';

export type ProductComparisonFooterProps = ComponentProps<'div'>;

/** The bar of actions under the table. */
export const ProductComparisonFooter = ({ className, ...props }: ProductComparisonFooterProps) => (
  <div
    data-slot="product-comparison-footer"
    className={clsxm(productComparisonFooterClasses, className)}
    {...props}
  />
);
